import { BreakpointObserver } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  output,
  Signal,
  signal,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { LoaderComponent } from '../loader/loader.component';

export type Align = 'left' | 'center' | 'right';

export interface TableColumn<T = unknown> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  width?: string;
  align?: Align;
  hideOnMobile?: boolean;
  cell?: (ctx: {
    row: T;
    value: unknown;
    rowIndex: number;
    colIndex: number;
    col: TableColumn<T>;
  }) => string;
  cellTemplate?: TemplateRef<unknown>;
  headerTemplate?: TemplateRef<unknown>;
}

@Component({
  selector: 'lib-dynamic-table',
  standalone: true,
  imports: [NgTemplateOutlet, LoaderComponent],
  templateUrl: './dynamic-table.component.html',
  styleUrls: ['./dynamic-table.component.scss'],
})
export class DynamicTableComponent<T = unknown> {
  private sanitizer = inject(DomSanitizer);
  private breakpointObserver = inject(BreakpointObserver);

  data = input<T[] | null>(null);
  columns = input<TableColumn<T>[]>([]);
  loading = input(false);
  noDataMessage = input<string>('No data available');
  trackByKey = input<keyof T | string | null>(null);
  rowClickable = input<boolean>(false);

  rowClick = output<T>();

  sortKey = signal<string | null>(null);
  sortDir = signal<'asc' | 'desc'>('asc');
  isMobile = signal(window.innerWidth < 768);

  constructor() {
    this.breakpointObserver
      .observe(['(max-width: 767px)'])
      .pipe(takeUntilDestroyed())
      .subscribe((result) => {
        this.isMobile.set(result.matches);
      });
  }

  visibleColumns = computed(() => {
    const cols = this.columns();
    if (!this.isMobile()) return cols;
    return cols.filter((col) => !col.hideOnMobile);
  });

  readonly processed: Signal<T[]> = computed(() => {
    const rows = [...(this.data() || [])];
    const key = this.sortKey();
    if (!key) return rows;

    const col = this.columns().find((c) => String(c.key) === key);
    const factor = this.sortDir() === 'asc' ? 1 : -1;

    return rows.sort((a, b) => {
      const av = this.raw(a, col);
      const bv = this.raw(b, col);
      const as = (av ?? '').toString().toLowerCase();
      const bs = (bv ?? '').toString().toLowerCase();
      return as < bs ? -1 * factor : as > bs ? 1 * factor : 0;
    });
  });

  setSort(col: TableColumn<T>) {
    const key = String(col.key);
    const sortable = col.sortable ?? false;
    if (!sortable) return;
    if (this.sortKey() !== key) {
      this.sortKey.set(key);
      this.sortDir.set('asc');
    } else {
      this.sortDir.set(this.sortDir() === 'asc' ? 'desc' : 'asc');
    }
  }

  ariaSort(col: TableColumn<T>) {
    const key = String(col.key);
    if (this.sortKey() !== key) return 'none';
    return this.sortDir() === 'asc' ? 'ascending' : 'descending';
  }

  raw(row: T, col?: TableColumn<T>): unknown {
    if (!col) return undefined;
    const k = String(col.key);

    // Support nested properties (e.g., 'companyProfile.name')
    if (k.includes('.')) {
      const keys = k.split('.');
      let value: unknown = row;
      for (const key of keys) {
        if (value == null) return undefined;
        value = (value as Record<string, unknown>)[key];
      }
      return value;
    }

    const record = row as Record<string, unknown>;
    return k in record ? record[k] : undefined;
  }

  text(row: T, col: TableColumn<T>): string {
    const v = this.raw(row, col);
    return v == null ? '' : String(v);
  }

  renderCell(row: T, col: TableColumn<T>, rowIndex: number, colIndex: number): SafeHtml | null {
    if (!col.cell) return null;
    const value = this.raw(row, col);
    const htmlString = col.cell({ row, value, rowIndex, colIndex, col }) ?? '';
    return this.sanitizer.bypassSecurityTrustHtml(htmlString);
  }

  // Rows are tracked by their trackByKey value (the row itself if it has none), or by position when no key is set
  trackBy = (index: number, item: T): unknown => {
    const key = this.trackByKey();
    if (key == null) return index;
    return (item as Record<string, unknown> | null)?.[String(key)] ?? item;
  };

  onRowClick(row: T) {
    this.rowClick.emit(row);
  }
}
